import { Injectable } from '@angular/core';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
  BookingHistoryInitialState,
  BookingHistoryLoad,
  BookingHistoryDownloadConfirmationPDF,
  BookingHistoryPreviewConfirmationPDF,
} from './booking-history.actions';
import { BookingHistoryService } from './booking-history.service';
import {
  BookingHistoryItem,
  BookingHistoryStateModel,
  BookingHistoryConfirmationResponseModel,
  BookingHistoryCriteria,
} from '../models';
import { Error, CustomHttpErrorResponse } from '../../../../../common/constants/error';
import {
  getBlobFromPDF,
  previewBlob,
  downloadBlob,
  NotificationService,
  getFileNameFromContentDispositionHeader,
} from '../../../../../common';
import { HttpResponse } from '@angular/common/http';

const initialState: BookingHistoryStateModel = {
  items: [],
  loaded: false,
};

@State<BookingHistoryStateModel>({
  name: 'bookingHistory',
  defaults: initialState,
})
@Injectable()
export class BookingHistoryState {
  @Selector()
  static getState(state: BookingHistoryStateModel): BookingHistoryStateModel {
    return state;
  }

  @Selector()
  static getItems(state: BookingHistoryStateModel): BookingHistoryItem[] {
    return state.items;
  }

  @Selector()
  static getLoaded(state: BookingHistoryStateModel): boolean {
    return state.loaded;
  }

  constructor(private bookingHistoryService: BookingHistoryService, private notificationService: NotificationService) {}

  @Action(BookingHistoryLoad)
  getBookingHistory(
    ctx: StateContext<BookingHistoryStateModel>,
    { payload }: BookingHistoryLoad,
  ): Observable<BookingHistoryItem[] | MatSnackBarRef<SimpleSnackBar>> {
    return this.bookingHistoryService.getBookingHistoryArray(payload).pipe(
      tap((response: BookingHistoryItem[]) => {
        ctx.patchState({
          items: response,
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(BookingHistoryInitialState)
  setInitialState(ctx: StateContext<BookingHistoryStateModel>): void {
    ctx.setState(initialState);
  }

  @Action(BookingHistoryPreviewConfirmationPDF)
  previewConfirmationPDF(
    ctx: StateContext<BookingHistoryStateModel>,
    { payload }: BookingHistoryPreviewConfirmationPDF,
  ): void {
    this.getConfirmationPDFInBlob(payload).subscribe((response: BookingHistoryConfirmationResponseModel) =>
      previewBlob(response.data),
    );
  }

  @Action(BookingHistoryDownloadConfirmationPDF)
  downloadConfirmationPDF(
    ctx: StateContext<BookingHistoryStateModel>,
    { payload }: BookingHistoryPreviewConfirmationPDF,
  ): void {
    this.getConfirmationPDFInBlob(payload).subscribe((response: BookingHistoryConfirmationResponseModel) =>
      downloadBlob(response.data, response.fileName),
    );
  }

  private getConfirmationPDFInBlob(
    params: BookingHistoryCriteria,
  ): Observable<BookingHistoryConfirmationResponseModel> {
    return this.bookingHistoryService.getConfirmationPDF(params).pipe(
      map((response: HttpResponse<Blob>) => {
        const fileName = getFileNameFromContentDispositionHeader(response);

        const confirmationResponse: BookingHistoryConfirmationResponseModel = {
          data: getBlobFromPDF(response.body),
          fileName,
        };

        return confirmationResponse;
      }),
    );
  }
}
